import { IDatabaseTableModel, IDatasourceDto } from "@kuika/kuika-cl-model";
import type { IScreenDesignContextState } from "../../../contexts/screen-design-context/screen-design-context";
import type { IInitialFilter } from "../../table-editor/table-rows-editor";
import { canBrowseTableRows, canEditTable, isTableEditable } from "./datasource-row-actions";

/**
 * What the table editor shows and where Browse Data lands.
 *
 * The table editor has two tabs over one table: Columns (the schema, as it has always been) and
 * Data (the row browser, embedded). Browse Data used to open the row browser in place of the
 * editor; it now opens the editor on its Data tab wherever the editor can host it, and falls back
 * to the standalone row browser where it cannot. The rules for that live here, as functions, so
 * the provider, the editor and the switcher chip ask the same questions.
 */

export type TableEditorTab = "columns" | "data";

/** The tabs in the order they are drawn — the panes slide towards the side the selection moved. */
export const TABLE_EDITOR_TABS: TableEditorTab[] = ["columns", "data"];

/**
 * A request for the open editor to show a tab. `requestId` is new on every request, so asking for
 * the tab the editor already asked for once (Browse Data twice, a second FK jump) is still news.
 */
export interface ITableEditorView {
  tab: TableEditorTab;
  /** Seeds the Data tab's filter — an FK "go to" lands on the referenced row. */
  initialFilter?: IInitialFilter;
  requestId: number;
}

/** What identifies a table: the tree hands over its own node, the editor holds the loaded model. */
export interface ITableReference {
  id?: string;
  name: string;
  schema?: string;
}

/** What Browse Data carries: the table, plus the filter an FK jump opens it on. */
export interface ITableBrowseRequest extends ITableReference {
  _initialFilter?: IInitialFilter;
}

/**
 * The same table. By id when both carry one — a rename in progress changes the name, not the id —
 * otherwise by name and schema. A missing schema stays missing: an Oracle or Postgres table without
 * one is not the `dbo` table of the same name (the same rule the row browser's view storage keys on).
 */
export const isSameTable = (a: ITableReference, b: ITableReference): boolean => {
  if (a.id && b.id) return a.id === b.id;
  return a.name === b.name && (a.schema ?? "") === (b.schema ?? "");
};

/**
 * The model the editor opens for a table. The loaded copy first, as the tree's Edit does; the
 * schema map only when its entry has columns — the slim path fills it with column-less rows, and
 * the editor treats a model without columns as a new table and adds an `Id` column to it.
 */
export const resolveTableModel = (
  reference: ITableReference,
  loaded: IDatabaseTableModel[],
  schemaTables: IDatabaseTableModel[]
): IDatabaseTableModel | undefined => {
  const fromLoaded: IDatabaseTableModel | undefined = loaded.find((table: IDatabaseTableModel): boolean =>
    isSameTable(table, reference)
  );
  if (fromLoaded) return fromLoaded;
  const fromSchema: IDatabaseTableModel | undefined = schemaTables.find((table: IDatabaseTableModel): boolean =>
    isSameTable(table, reference)
  );
  return fromSchema && (fromSchema.columns?.length ?? 0) > 0 ? fromSchema : undefined;
};

/** The editor opens only on the managed database, and the row endpoints only answer there. */
export const canHostRowsInTableEditor = (datasource: IDatasourceDto): boolean =>
  canEditTable(datasource) && canBrowseTableRows(datasource);

export interface IDataTabAvailability {
  isCreating: boolean;
  datasource?: IDatasourceDto;
}

/** Why the Data tab is off, or undefined when it is on. Said on the tab itself. */
export const getDataTabDisabledReason = ({ isCreating, datasource }: IDataTabAvailability): string | undefined => {
  if (isCreating) return "Create the table first — its rows appear here once it exists.";
  if (!datasource || !canHostRowsInTableEditor(datasource)) return "Rows can be browsed on the managed database only.";
  return undefined;
};

/** The open editor already holds the table: it only has to show its Data tab. */
export interface IBrowseInOpenEditor {
  kind: "same-table";
}

/** Open the table's editor on its Data tab. */
export interface IBrowseInTableEditor {
  kind: "table-editor";
  table: IDatabaseTableModel;
  /** A table outside the project's schema opens read-only, as the tree's Edit opens it. */
  readOnly: boolean;
}

/** The standalone row browser: the editor cannot host this table's rows. */
export interface IBrowseStandalone {
  kind: "standalone";
}

export type BrowseDataRoute = IBrowseInOpenEditor | IBrowseInTableEditor | IBrowseStandalone;

/** Where Browse Data on a table goes, given what is open. */
export const routeBrowseData = (
  state: IScreenDesignContextState,
  datasource: IDatasourceDto,
  table: ITableReference
): BrowseDataRoute => {
  if (!canHostRowsInTableEditor(datasource)) return { kind: "standalone" };

  const open: IDatabaseTableModel | undefined = state.editingTable;
  if (open && state.creatingTablesDatasource?.id === datasource.id && isSameTable(open, table)) {
    return { kind: "same-table" };
  }

  const resolved: IDatabaseTableModel | undefined = resolveTableModel(
    table,
    state.databaseDiagramData?.leftMenuData?.tables ?? [],
    state.schemas?.get(datasource.id)?.tables ?? []
  );
  if (!resolved) return { kind: "standalone" };
  return { kind: "table-editor", table: resolved, readOnly: !isTableEditable(state, resolved) };
};
